import { splitTextWithUrls } from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import * as Styled from './url-parser.style';

export type LinkBaseProps = {
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  target?: string;
  rel?: string;
};

export type UrlParserProps = Omit<
  React.ComponentProps<typeof Typography>,
  'children'
> & {
  value: string;
  linkComponent: React.ComponentType<LinkBaseProps>;
  respectNewLines?: boolean;
  explicitTextCursor?: boolean;
  hasPaddingRight?: boolean;
};

export const UrlParser: React.FC<UrlParserProps> = ({
  value,
  linkComponent: Link,
  respectNewLines,
  explicitTextCursor,
  hasPaddingRight,
  ...rest
}) => {
  const elements = useMemo(() => {
    const segments = splitTextWithUrls(value);

    if (!segments.length) {
      return [];
    }

    const hasAnyUrl = segments.some((segment) => segment.kind === 'url');
    if (!hasAnyUrl) {
      return [value];
    }

    return segments.map((segment, index) => {
      if (segment.kind === 'text') {
        return segment.value;
      }
      return (
        <Link
          key={`url-${index}`}
          href={segment.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {segment.value}
        </Link>
      );
    });
  }, [value, Link]);

  return (
    <Styled.UrlParser
      $explicitTextCursor={!!explicitTextCursor}
      $hasPaddingRight={!!hasPaddingRight}
      $respectNewLines={!!respectNewLines}
      {...rest}
    >
      {elements.length ? elements : value}
    </Styled.UrlParser>
  );
};
